import type { CashbookFundingSourcePostable } from '@finvox/shared/cashbook';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';

type Props = {
  value: CashbookFundingSourcePostable | '';
  onChange: (v: CashbookFundingSourcePostable) => void;
  disabled?: boolean;
  id?: string;
  required?: boolean;
  className?: string;
};

/** Stacked From drawer / From treasury choice — mobile-friendly. */
export function CashbookFundingSourcePicker({
  value,
  onChange,
  disabled,
  id = 'cashbook-funding-source',
  required,
  className
}: Props) {
  const { t } = useTranslation();

  const options: { id: CashbookFundingSourcePostable; label: string }[] = [
    { id: 'drawer', label: t('cashbook.funding.fromDrawer') },
    { id: 'treasury', label: t('cashbook.funding.fromTreasury') }
  ];

  return (
    <fieldset className={cn('w-full', className)} disabled={disabled}>
      <legend className="text-xs text-[var(--fv-text-muted)]">
        {t('cashbook.funding.label')}
        {required ? ' *' : ''}
      </legend>
      <div className="mt-2 flex flex-col gap-2 sm:flex-row" role="radiogroup" aria-required={required}>
        {options.map((opt) => {
          const selected = value === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              id={`${id}-${opt.id}`}
              role="radio"
              aria-checked={selected}
              disabled={disabled}
              onClick={() => onChange(opt.id)}
              className={cn(
                'min-h-11 w-full rounded-lg border px-4 py-3 text-sm font-medium transition-colors',
                selected
                  ? 'border-[var(--fv-accent)] bg-[var(--fv-accent-muted,var(--fv-surface-2))] text-[var(--fv-text)]'
                  : 'border-[var(--fv-border)] bg-[var(--fv-surface)] text-[var(--fv-text)] hover:border-[var(--fv-text-muted)]'
              )}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
